class RecentlyViewed extends HTMLElement {
  constructor() {
    super();
    this.productId = null;
    this.recentlyViewedProduct = {
      filled: false,
      productId: "",
      productObj: {},
      productElement: null,
    };
    this.recentlyViewedProducts = [];
    this.localStorageKey = "recently_viewed_products";
    this.maxProductsToShow = 6;
    this.maxArrayLength = 7;
    this.shopUrl = null;
  }

  static map = [
    {
      name: "tags",
      selector: ".product-badge.product-badge--discount",
    },
    {
      name: "title",
      selector: ".product-card__meta .product-card__title a",
    },
    {
      name: "compare_at_price",
      selector: ".product-card__price .strike-through-price",
    },
    {
      name: "price",
      selector: ".product-card__price > span:first-child",
    },
    {
      name: "featured_image",
      selector: "media-loader > img:first-child",
    },
    {
      name: "images[1]",
      selector: "media-loader > img:nth-child(2)",
    },
  ];

  setAttributesAndElements() {
    let productNode = this.querySelector("[data-product-card]");
    this.productId = this.getAttribute("data-product-id");
    this.shopUrl = this.getAttribute("data-shop-url");

    if (productNode) {
      productNode = productNode.innerHTML;
    }

    if (
      window.recentlyViewed &&
      Object.keys(window.recentlyViewed.product).length > 0 &&
      productNode &&
      this.productId
    ) {
      this.recentlyViewedProduct = {
        filled: true,
        productId: this.productId,
        productObj: window.recentlyViewed.product,
        productElement: productNode,
      };
    }

    this.setLocalStorage();
  }

  setLocalStorage() {
    if (this.recentlyViewedProduct.filled) {
      this.recentlyViewedProducts = this.getLocalStorage();
      if (!this.compareProducts(this.recentlyViewedProducts)) {
        this.recentlyViewedProducts.push(this.recentlyViewedProduct);
      } else {
        this.recentlyViewedProducts = this.rewriteProductToArr(this.recentlyViewedProducts, this.recentlyViewedProduct);
      }

      if (this.recentlyViewedProducts.length > this.maxArrayLength) {
        this.recentlyViewedProducts = this.cutProductsArray(this.recentlyViewedProducts);
      }

      localStorage.setItem(this.localStorageKey, JSON.stringify(this.recentlyViewedProducts));
    }
  }

  rewriteProductToArr(arr, obj) {
    const newArr = arr.filter(({productId}) => productId !== this.productId);
    newArr.push(obj);
    return newArr;
  }

  cutProductsArray(arr) {
    return arr.slice(-this.maxArrayLength);
  }

  getLocalStorage() {
    const recentlyViewedProducts = JSON.parse(localStorage.getItem(this.localStorageKey));
    return recentlyViewedProducts || [];
  }

  cloneElement(element) {
    const clonedElement = element.cloneNode(true);
    return clonedElement;
  }

  compareProducts(arr) {
    return arr.some(({productId}) => productId === this.productId);
  }

  async renderCards() {
    const slider = this.querySelector("horizontal-slider");
    const productsContainer = slider.querySelector(".product-carousel__list");
    if (productsContainer) {
      const productWrapper = document.createElement("div");
      productWrapper.classList.add("product-carousel__item");
      productWrapper.setAttribute("role", "group");
      productWrapper.setAttribute("data-horizontal-slider-slide", "");
      let counter = 0;

      const slideLabelTemplate = this.getAttribute("data-slide-label-template") || "slide __CURRENT__ of __TOTAL__";
      const eligibleProducts = this.recentlyViewedProducts.filter(({productId}) => productId !== this.productId);
      const total = Math.min(eligibleProducts.length, this.maxProductsToShow);

      for (const {productElement, productId, productObj} of [...this.recentlyViewedProducts].reverse()) {
        if (productId === this.productId) continue;
        if (counter >= this.maxProductsToShow) break;

        const freshObj = await this.loadProductByHandle(productObj.handle);
        const diff = this.compareProductsWithDiff({}, freshObj);

        const card = document.createElement("product-card");
        card.classList.add("product-card");
        card.innerHTML = productElement;

        if (Object.keys(diff).length > 0) {
          this.updateCardWithDiff(card, freshObj, diff);
        }

        const newProductWrapper = productWrapper.cloneNode(true);
        counter++;
        newProductWrapper.setAttribute(
          "aria-label",
          slideLabelTemplate.replace("__CURRENT__", counter).replace("__TOTAL__", total)
        );
        newProductWrapper.appendChild(card);
        productsContainer.appendChild(newProductWrapper);
      }

      const title = this.querySelector(".product-carousel__title");
      if (title) {
        title.style.display = "block";
      }

      this.sliderInit(slider);
    }
  }

  sliderInit(slider) {
    if (typeof slider._init === "function") {
      slider._init();
      slider._render();
    } else {
      setTimeout(() => {
        slider._init();
        slider._render();
      }, 2000);
    }
  }

  async loadProductByHandle(handle) {
    if (this.shopUrl === null) return;
    return await fetch(`${this.shopUrl}/products/${handle}.js`)
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Product ${handle} not found`);
        }
        return response.json();
      })
      .then((product) => {
        return product;
      })
      .catch((error) => {
        console.error("Error loading product:", error);
        return null;
      });
  }

  checkProductsCount() {
    if (this.recentlyViewedProducts.length < 1) {
      return false;
    } else if (this.recentlyViewedProducts.length <= 1 && this.compareProducts(this.recentlyViewedProducts)) {
      return false;
    } else {
      return true;
    }
  }

  connectedCallback() {
    this.setAttributesAndElements();
    if (this.checkProductsCount()) {
      this.renderCards();
    }
  }

  getImageFilename(url = "") {
    try {
      return url.split("/").pop().split("?")[0];
    } catch (e) {
      return url;
    }
  }

  compareProductsWithDiff(p1, p2) {
    const differences = {};

    const fieldsToCompare = ["compare_at_price", "price", "title"];

    for (const field of fieldsToCompare) {
      if (p1[field] !== p2[field]) {
        differences[field] = {
          from: p1[field],
          to: p2[field],
        };
      }
    }

    const getDiscountTags = (tags) => (tags || []).filter((tag) => tag.toLowerCase().includes("discount")).sort();

    const tags1 = getDiscountTags(p1.tags);
    const tags2 = getDiscountTags(p2.tags);

    if (tags1.length !== tags2.length || !tags1.every((t, i) => t === tags2[i])) {
      differences.tags = {
        from: tags1,
        to: tags2,
      };
    }

    const img1 = this.getImageFilename(p1.images?.[1]);
    const img2 = this.getImageFilename(p2.images?.[1]);

    if (img1 !== img2) {
      differences["images[1]"] = {
        from: p1.images?.[1],
        to: p2.images?.[1],
      };
    }

    const featured1 = this.getImageFilename(p1.featured_image);
    const featured2 = this.getImageFilename(p2.featured_image);

    if (featured1 !== featured2) {
      differences["featured_image"] = {
        from: p1.featured_image,
        to: p2.featured_image,
      };
    }

    return differences;
  }

  async updateCardWithDiff(cardElement, freshObj, diff) {
    const map = this.constructor.map;

    for (const field of map) {
      const {name, selector} = field;

      if (!diff[name]) continue;

      const el = cardElement.querySelector(selector);
      if (!el && name !== "tags") continue;

      let newValue = freshObj;

      if (name.includes("[")) {
        const match = name.match(/^([^\[]+)\[(\d+)\]$/);
        if (match) {
          const prop = match[1];
          const index = parseInt(match[2]);
          newValue = freshObj[prop]?.[index];
        }
      } else {
        newValue = freshObj[name];
      }

      if (name === "price") {
        const priceValues = await this.fetchFormattedProductPrice(freshObj.handle);
        const priceWrapper = el.closest(".product-card__price");
        if (priceWrapper) {
          const comparedEl = priceWrapper.querySelector(".strike-through-price");
          if (comparedEl) {
            comparedEl.textContent = priceValues[1];
          }
          el.textContent = priceValues[0];
        }
        continue;
      }

      if (name === "tags") {
        const discountTag = (freshObj.tags || []).find((tag) => tag.toLowerCase().includes("discount"));

        if (discountTag) {
          let badgeEl = cardElement.querySelector(selector);
          if (!badgeEl) {
            badgeEl = document.createElement("div");
            badgeEl.className = "product-badge product-badge--discount";
            const mediaWrap = cardElement.querySelector(".product-card__media");
            if (mediaWrap) mediaWrap.prepend(badgeEl);
          }
          badgeEl.textContent = discountTag.replace(/(\d+)(discount)/i, "$1% OFF");
        }
        continue;
      }

      if (el.tagName === "IMG" && typeof newValue === "string") {
        el.src = newValue;
      }

      else if (typeof newValue === "string" || typeof newValue === "number") {
        el.textContent = newValue;
      }
    }
  }

  async fetchFormattedProductPrice(handle) {
    try {
      const res = await fetch(`/products/${handle}?section_id=main-product`);
      const html = await res.text();

      const doc = new DOMParser().parseFromString(html, "text/html");

      const priceEl =
        doc.querySelector(".product__price > .price-highlight") ||
        doc.querySelector(".price-item--regular") ||
        doc.querySelector(".product__price span") ||
        doc.querySelector("[data-product-price]");

      const comparedPrice = doc.querySelector(".product__price > .strike-through-price");
      const priceStylesValues = window.getComputedStyle(document.querySelector(".product__details .product__price .strike-through-price__desc")).display;
      
      const price = priceEl?.textContent?.trim();
      let compareAtPrice = comparedPrice?.textContent?.trim()
      if (priceStylesValues === "none") {
        compareAtPrice = compareAtPrice.replace("Comp. value", "");
        compareAtPrice = compareAtPrice.replace("\n", "").trim();
      }

      return [price, compareAtPrice || null] || null;
    } catch (error) {
      console.error("Error during loading the price:", error);
      return null;
    }
  }
}

if (!window.customElements.get("recently-viewed")) {
  window.customElements.define("recently-viewed", RecentlyViewed);
}